<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
  <a
    [href]="linkToSource"
    target="_blank">
    <go-button buttonVariant="neutral"> View on GitHub </go-button>
  </a>
</header>

<section class="go-container">

  <go-card class="go-column go-column--100" id="usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Usage</h2>
      <go-copy cardId="usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <!-- Intro -->
        <p class="go-body-copy go-body-copy--no-margin">
          The <code class="code-block--inline">&lt;go-modal&gt;</code> component should be the only instance
          of a modal in the app.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h3 class="go-heading-3">Bindings</h3>
        <code [highlight]="componentBindings"></code>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">modalTitle</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          <code class="code-block--inline">modalTitle</code> sets the title for the modal.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">modalSize</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          <code class="code-block--inline">modalSize</code> defines the width of the modal. Currently defaulted to <code class="code-block--inline">lg</code>.
        </p>
      </div>

      <div class="go-column go-column--100">
        <h4 class="go-heading-4">noContentPadding</h4>
        <p class="go-body-copy go-body-copy--no-margin">
          <code class="code-block--inline">noContentPadding</code> determines whether or not to render padding on the modal content. Defaults to false.
        </p>
      </div>

      <div class="go-column go-column--100 go-column--no-padding">
        <h3 class="go-heading-3">Setup</h3>
        <p class="go-body-copy">To use the modal anywhere in the app, you must first add it to the app level. This should only be done once per application.</p>
        <ol class="go-list go-list--ordered">
          <li>
            <p class="go-body-copy">
              Import the <code class="code-block--inline">GoModalModule</code> into <code class="code-block--inline">app.module.ts</code>
            </p>
            <code [highlight]="appModuleImport"></code>
          </li>
          <li>
            <p class="go-body-copy">
              Add the <code class="code-block--inline">&lt;go-modal&gt;</code> element to <code class="code-block--inline">app.component.html</code>.
            </p>
          </li>
        </ol>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="service-usage">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Using the Modal service</h2>
      <go-copy cardId="service-usage" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100 go-column--no-padding">
        <p class="go-body-copy">
          For this example, the component we want to render in the modal is <code class="code-block--inline">ModalTestComponent</code>.
          This component lives in the <code class="code-block--inline">UiKitModule</code>.
          To make use of the modal in our component:
        </p>
        <ol class="go-list go-list--ordered">
          <li>
            <p class="go-body-copy">
              In the module that imports your component, import the <code class="code-block--inline">GoModalModule</code>
              and add the <code class="code-block--inline">GoModalService</code> to the providers.
            </p>
            <code [highlight]="ex_UiKitModule"></code>
          </li>
          <li>
            <p class="go-body-copy">
              In the component that is telling the modal to open, import the <code class="code-block--inline">GoModalService</code>,
              and the component that is being rendered inside the modal.
            </p>
            <code [highlight]="ex_ModalDocsImports"></code>
          </li>
          <li>
            <p class="go-body-copy">
              To open the modal, use the service to call <code class="code-block--inline">openModal</code>. This method takes the component type
              (ModalTestComponent in our case), and the bindings for that component as an object.
            </p>
            <code [highlight]="ex_ModalDocsOpenModal"></code>
          </li>
          <li>
            <p class="go-body-copy">
              Lastly, the button that triggers the openModal method.
            </p>
            <div class="go-container">
              <div class="go-column go-column--50">
                <h4 class="go-heading-4">component.html</h4>
                <code [highlight]="ex_ModalDocsHtml"></code>
              </div>
              <div class="go-column go-column--50">
                <h4 class="go-heading-4">View</h4>
                <go-button (handleClick)="openModal()">Click Me</go-button>
              </div>
            </div>
          </li>
        </ol>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="modal-backdrop">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Modal Backdrop</h2>
      <go-copy cardId="modal-backdrop" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          By default clicking on the backdrop behind the modal will not close the modal. If you wish to enable closing
          the modal by clicking on the backdrop the <code class="code-block--inline">closeWithBackdrop</code> binding can
          be used to enable it.
        </p>
      </div>

      <div class="go-column go-column--75">
        <h4 class="go-heading-4">Code</h4>
        <code [highlight]="ex_ModalDocsCloseWithBackdrop"></code>
      </div>

      <div class="go-column go-column--25">
        <h4 class="go-heading-4">View</h4>
        <go-button (handleClick)="openCloseWithBackdropModal()">Click Me</go-button>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="modal-close-icon">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Modal Close Icon</h2>
      <go-copy cardId="modal-close-icon" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--100">
        <p class="go-body-copy go-body-copy--no-margin">
          By default there is a close icon in the upper right corner of a modal. In certain cases where
          a response is required by a user we might want to remove this icon to force a user to make a decision between
          a set of choices. This icon can be disabled through the <code class="code-block--inline">showCloseIcon</code>
          binding.
        </p>
      </div>

      <div class="go-column go-column--75">
        <h4 class="go-heading-4">Code</h4>
        <code [highlight]="ex_ModalDocsNoCloseIcon"></code>
      </div>

      <div class="go-column go-column--25">
        <h4 class="go-heading-4">View</h4>
        <go-button (handleClick)="openNoCloseIconModal()">Click Me</go-button>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="sizes">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Modal Sizes</h2>
      <go-copy cardId="sizes" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--75">
        <h4 class="go-heading-4">LG Code (Default)</h4>
        <code [highlight]="ex_ModalDocsOpenLgModal"></code>
      </div>

      <div class="go-column go-column--25">
        <h4 class="go-heading-4">LG View</h4>
        <go-button (handleClick)="openLgModal()">Click Me</go-button>
      </div>

      <div class="go-column go-column--75">
        <h4 class="go-heading-4">XL Code</h4>
        <code [highlight]="ex_ModalDocsOpenXlModal"></code>
      </div>

      <div class="go-column go-column--25">
        <h4 class="go-heading-4">XL View</h4>
        <go-button (handleClick)="openXlModal()">Click Me</go-button>
      </div>

    </div>
  </go-card>

  <go-card class="go-column go-column--100" id="padding">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Modal Padding</h2>
      <go-copy cardId="padding" goCopyCardLink></go-copy>
    </ng-container>
    <div class="go-container" go-card-content>

      <div class="go-column go-column--75">
        <h4 class="go-heading-4">Code</h4>
        <code [highlight]="ex_ModalDocsNoPadding"></code>
      </div>

      <div class="go-column go-column--25">
        <h4 class="go-heading-4">View</h4>
        <go-button (handleClick)="openNoPaddingModal()">Click Me</go-button>
      </div>

    </div>
  </go-card>
</section>
